import { getUserData, clearUserData } from "./utils/storage";
import { Layout } from "./components";
import { useEffect, useState, useCallback } from "react";
import { Navigate, Outlet, useNavigate } from "react-router";
import { useAppDispatch } from "./store";
import { CircularProgress, Box } from "@mui/material";
import { getDetails } from "services/MerchantHostApi";
import { actions } from "./components/layout/appLayoutSlice";
import { updateInitialState } from "slice/merchantSlice";
import { updateHostState } from "slice/hostSlice";
import ApprovalScreen from "Approval";
import { ALLOWED_ROUTES } from "./utils";
import { toast } from "react-toastify"; 
import { 
  updateActivityTimestamp, 
  shouldLogoutDueToInactivity,  
  isCurrentDevice 
} from "sessionManager";

const PrivateRoute = ({ restricted }: { restricted: boolean }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Ensure valid user data
  const userData = getUserData() || {};
  const userId = userData?.userId || null;
  const userType = userData?.userType || null;
  const [approvalStatus, setApprovalStatus] = useState<string>("APPROVED");

  const [isFetched, setIsFetched] = useState(false);
  const [error, setError] = useState(false);

  // Session management
  const checkUserSession = useCallback(() => {
    // First verify it's the same device
    if (!isCurrentDevice()) {
      clearUserData();
      navigate('/', { replace: true });
      toast.info("Session expired. Please sign in again.", { position: "top-right" });
      return false;
    }
    
    // Then check for inactivity
    if (shouldLogoutDueToInactivity()) {
      clearUserData();
      // Reset Redux state
      navigate('/', { replace: true });
      toast.info("You've been logged out due to inactivity", { position: "top-right" });
      return false;
    }
    
    // // Update timestamp on successful check
    // updateActivityTimestamp();
    return true;
  }, [navigate, dispatch]);

  const fetchUserData = useCallback(
    async (signal: AbortSignal) => {
      try {
        if (!userId || !userType) return;

        // Check session before making API call
        if (!checkUserSession()) return;

        const fetchType = userType === "merchant" ? "Merchants" : "Hosts";
        const userDetails = await getDetails(fetchType, userId, signal);

        if (!userDetails) {
          throw new Error("Invalid user data received");
        }

        // Update state based on user type
        const updateAction =
          userType === "merchant" ? updateInitialState : updateHostState;

        const status =
          userType === "merchant"
            ? userDetails?.merchantApprovalStatus
            : userDetails?.hostApprovalStatus;
        setApprovalStatus(status);
        updateActivityTimestamp();

        dispatch(updateAction(userDetails));
        dispatch(actions.setUserDetails(userDetails));
      } catch (err: any) {
        if (err.name !== "AbortError") {
          console.error("User data fetch error:", err);
          setError(err.message || "Failed to load user data");
        }
      } finally {
        setIsFetched(true);
      }
    },
    [userId, userType, dispatch, checkUserSession]
  );

  useEffect(() => {
    if (!userId || !userType) {
      setError(true); // Ensure redirect if no valid user data
      return;
    }

    // Check session first
    if (!checkUserSession()) {
      return;
    }

    const controller = new AbortController();
    if (!isFetched) {
      fetchUserData(controller.signal);
    }
    
    const handleUserActivity = () => {
      updateActivityTimestamp();
    };
    
    return () => controller.abort();
  }, [userId, userType, isFetched, checkUserSession, fetchUserData]);

  // Redirect if user data is missing or fetch fails
  if (!userId || !userType || error) return <Navigate to="/" />;

  if (!isFetched) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (restricted && approvalStatus !== "APPROVED") {
    return (
      <Layout>
        <ApprovalScreen />
      </Layout>
    );
  }

  const allowedRoutes = ALLOWED_ROUTES[userType] || [];
  if (!allowedRoutes.includes(location.pathname)) {
    return <Navigate to="/not-found" replace />;
  }

  return (
    <Layout>
      <Outlet />
    </Layout>
  );
};

export default PrivateRoute;